import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
const CENTER: [number, number] = [47.3087, -122.0032]; // Black Diamond, WA
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
function getZoneInfo(miles: number) {
  if (miles <= 7) return {
    zone: 1,
    fee: 40
  };
  if (miles <= 13) return {
    zone: 2,
    fee: 75
  };
  if (miles <= 18) return {
    zone: 3,
    fee: 120
  };
  if (miles <= 22) return {
    zone: 4,
    fee: 150
  };
  return {
    zone: null,
    fee: 0
  }; // Custom pricing needed
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo
  } = useCart();
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const totalDeliveryFee = deliveryFee * deliveryCount;
  const finalTotal = totalPrice + totalDeliveryFee;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
          const zoneInfo = getZoneInfo(distance);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const surcharge = !item.product.toLowerCase().includes('bark') && item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        return `${item.product} (${item.category}) — ${item.quantity} yds × $${effectivePrice}/yd = $${item.quantity * effectivePrice}`;
      }).join('\n');
      await sendFormEmail({
        subject: '253Gravel — New Order Submission',
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Delivery Fee': totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE',
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: `Hi ${name}! Thank you for your order request with 253 Gravel! We've received your details and will contact you shortly to verify your order and confirm delivery.\n\nOrder Summary:\n- Order Total: ${finalTotal.toFixed(2)}\n- Delivery Address: ${addressInput}\n- Requested Date: ${deliveryDate}\n- Time Window: ${deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12-4 PM)' : 'Flexible'}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The 253 Gravel Team`,
        'Delivery Date': deliveryDate,
        'Delivery Time': deliveryTime,
        'Special Instructions': instructions || 'None',
        'Payment Method': paymentMethod === 'card' ? 'Pay Online' : 'Pay on Delivery'
      });
    } catch (err) {
      console.error('Order submission error:', err);
    } finally {
      setIsProcessingPayment(false);
    }
    setView('success');
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    clearCart();
    closeCart();
  };
  const inputClass = 'w-full px-4 py-3.5 bg-stone-100 dark:bg-[#555555] border border-stone-300 dark:border-[#606060] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all text-base font-medium';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/#/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  return <AnimatePresence data-id="element-120">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-121" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" data-id="element-122">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full max-w-5xl bg-white dark:bg-[#3d3d3d] shadow-2xl flex flex-col rounded-2xl border border-brand-lightgray dark:border-[#4b4b4b] max-h-[90vh] overflow-hidden pointer-events-auto" data-id="element-123">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-brand-lightgray dark:border-[#4b4b4b] shrink-0" data-id="element-124">
                <div className="flex items-center space-x-4" data-id="element-125">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-lg hover:bg-brand-lightgray dark:hover:bg-[#5a5a5a] mr-1" data-id="element-126">
                      <ArrowLeft className="w-6 h-6" data-id="element-127" />
                    </button>}
                  <ShoppingCart className="w-7 h-7 text-brand-charcoal dark:text-white" data-id="element-128" />
                  <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-129">
                    {view === 'cart' && 'Your Cart'}
                    {view === 'checkout' && 'Checkout'}
                    {view === 'success' && 'Order Submitted'}
                  </h2>
                  {view === 'cart' && <span className="bg-brand-lightgray dark:bg-[#4a4a4a] text-brand-charcoal dark:text-stone-200 text-sm font-bold px-3 py-1.5 rounded-lg" data-id="element-130">
                      {items.length} {items.length === 1 ? 'item' : 'items'}
                    </span>}
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-brand-lightgray dark:hover:bg-[#5a5a5a]" data-id="element-131">
                  <X className="w-6 h-6" data-id="element-132" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-133">
                  {/* Items */}
                  <div className="p-8" data-id="element-134">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-135">
                        <div className="w-28 h-28 bg-brand-lightgray/50 dark:bg-[#4a4a4a] rounded-full flex items-center justify-center" data-id="element-136">
                          <ShoppingCart className="w-12 h-12 text-brand-muted opacity-50" data-id="element-137" />
                        </div>
                        <div data-id="element-138">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-139">
                            Your cart is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-140">
                            Looks like you haven't added any materials to your
                            cart yet.
                          </p>
                        </div>
                        <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-[#4a4a4a] hover:bg-brand-charcoal/90 dark:hover:bg-[#555555] text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors" data-id="element-141">
                          Browse Materials
                        </button>
                      </div> : <div className="space-y-5" data-id="element-142">
                        {items.map(item => <div key={item.id} className="flex flex-col sm:flex-row gap-5 p-5 bg-brand-lightgray/30 dark:bg-[#333333] rounded-2xl border border-brand-lightgray dark:border-[#4b4b4b]" data-id="element-143">
                            <div className="flex-1" data-id="element-144">
                              <p className="text-sm font-bold text-brand-muted mb-1.5 uppercase tracking-wider" data-id="element-145">
                                {item.category}
                              </p>
                              <h4 className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white mb-1.5" data-id="element-146">
                                {item.product}
                              </h4>
                              {(() => {
                      const isBarkMulch = item.product.toLowerCase().includes('bark');
                      const surcharge = !isBarkMulch && item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      return <p className="text-base text-brand-muted dark:text-stone-300 font-medium" data-id="element-147">
                                    ${effectivePrice} / yard
                                    {!isBarkMulch && item.quantity < 10 && <span className="text-sm text-brand-orange ml-1.5" data-id="element-148">
                                        (small load · ${item.pricePerYard}/yd at
                                        10+)
                                      </span>}
                                  </p>;
                    })()}
                            </div>
                            <div className="flex items-center justify-between sm:flex-col sm:items-end sm:justify-between gap-4" data-id="element-149">
                              {(() => {
                      const isBarkMulch = item.product.toLowerCase().includes('bark');
                      const surcharge = !isBarkMulch && item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      return <p className="font-heading font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-150">
                                    ${item.quantity * effectivePrice}
                                  </p>;
                    })()}
                              <div className="flex items-center space-x-3" data-id="element-151">
                                {(() => {
                        const isBarkMulch = item.product.toLowerCase().includes('bark');
                        const fullLoad = isBarkMulch ? 15 : 12;
                        const isFullLoadMultiple = item.quantity % fullLoad === 0;
                        const loadCount = item.quantity / fullLoad;
                        const handleMinus = () => {
                          if (isFullLoadMultiple && loadCount > 1) {
                            updateQuantity(item.id, item.quantity - fullLoad);
                          } else {
                            updateQuantity(item.id, item.quantity - 1);
                          }
                        };
                        const handlePlus = () => {
                          if (isFullLoadMultiple) {
                            updateQuantity(item.id, item.quantity + fullLoad);
                          } else {
                            updateQuantity(item.id, item.quantity + 1);
                          }
                        };
                        return <div className="flex items-center bg-white dark:bg-[#4a4a4a] border border-brand-lightgray dark:border-[#5a5a5a] rounded-lg overflow-hidden" data-id="element-152">
                                      <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-brand-lightgray dark:hover:bg-[#5a5a5a] transition-colors" data-id="element-153">
                                        <Minus className="w-5 h-5" data-id="element-154" />
                                      </button>
                                      <div className="w-18 flex flex-col items-center justify-center py-1.5 px-3" data-id="element-155">
                                        <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-156">
                                          {isFullLoadMultiple ? loadCount : item.quantity}
                                        </span>
                                        {isFullLoadMultiple && <span className="text-[10px] text-brand-muted dark:text-stone-200 uppercase tracking-wider mt-0.5 font-bold" data-id="element-157">
                                            {loadCount === 1 ? 'Full Load' : 'Full Loads'}
                                          </span>}
                                      </div>
                                      <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-brand-lightgray dark:hover:bg-[#5a5a5a] transition-colors" data-id="element-158">
                                        <Plus className="w-5 h-5" data-id="element-159" />
                                      </button>
                                    </div>;
                      })()}
                                <button onClick={() => removeFromCart(item.id)} className="p-2.5 text-brand-muted hover:text-brand-orange transition-colors" title="Remove item" data-id="element-160">
                                  <Trash2 className="w-5 h-5" data-id="element-161" />
                                </button>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Delivery Details */}
                  {items.length > 0 && <div className="px-8 pb-8 border-t border-brand-lightgray dark:border-[#4b4b4b] pt-8" data-id="element-162">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-163">
                        Delivery Details
                      </h3>
                      <div className="space-y-4" data-id="element-164">
                        {/* Address */}
                        <div data-id="element-165">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-166">
                            <span className="flex items-center" data-id="element-167">
                              <MapPin className="w-5 h-5 mr-2 text-brand-muted" data-id="element-168" />
                              Delivery Address
                            </span>
                            {isCalculatingDelivery && <span className="text-sm text-brand-orange flex items-center" data-id="element-169">
                                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" data-id="element-170" />
                                Calculating...
                              </span>}
                          </label>
                          <input type="text" value={addressInput} onChange={e => setAddressInput(e.target.value)} placeholder="123 Main St, Tacoma, WA" autoComplete="street-address" className={inputClass} required data-id="element-171" />
                          {addressError && <p className="text-sm text-red-500 mt-1.5 font-medium" data-id="element-172">
                              {addressError}
                            </p>}
                          {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 mt-1.5 flex items-center font-medium" data-id="element-173">
                                <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-174" />
                                Zone {deliveryZone} (
                                {deliveryFee === 0 ? 'Free Delivery' : deliveryCount > 1 ? `+$${totalDeliveryFee.toFixed(0)} for ${deliveryCount} deliveries` : `+$${deliveryFee} Delivery`}
                                ) —{' '}
                                <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors" data-id="element-175">
                                  View Zones
                                </a>
                              </p>}
                          {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange mt-1.5 flex items-center font-medium" data-id="element-176">
                                <Info className="w-4 h-4 mr-1" data-id="element-177" />
                                Outside standard zones. We'll call with a custom
                                quote.
                              </p>}
                        </div>

                        {/* Name */}
                        <div data-id="element-178">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-179">
                            <FileText className="w-5 h-5 mr-2 text-brand-muted" data-id="element-180" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${isAddressValid && !isNameValid ? 'ring-2 ring-[#05894d] border-[#05894d]' : ''}`} required data-id="element-181" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-182">
                          <div data-id="element-183">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-184">
                              <Phone className="w-5 h-5 mr-2 text-brand-muted" data-id="element-185" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#05894d] border-[#05894d]' : ''}`} required data-id="element-186" />
                          </div>
                          <div data-id="element-187">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-188">
                              <Mail className="w-5 h-5 mr-2 text-brand-muted" data-id="element-189" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#05894d] border-[#05894d]' : ''}`} required data-id="element-190" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-191">
                          <div className="relative" data-id="element-192">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-193">
                              <CalendarDays className="w-5 h-5 mr-2 text-brand-muted" data-id="element-194" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#05894d] border-[#05894d]' : ''}`} data-id="element-195">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-196">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-197" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-198">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white dark:bg-[#4a4a4a] border border-stone-200 dark:border-[#555555] rounded-2xl shadow-2xl p-5" data-id="element-199">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-200">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-stone-100 dark:hover:bg-[#555555] rounded-lg transition-colors" data-id="element-201">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-202" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-203">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-stone-100 dark:hover:bg-[#555555] rounded-lg transition-colors" data-id="element-204">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-205" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-206">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-207">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-208">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-209" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                return <button key={dateStr} type="button" disabled={isPast} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} className={`text-base font-bold py-2 rounded-xl transition-colors ${isPast ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : isSelected ? 'bg-brand-orange text-white' : isToday ? 'bg-[#05894d] text-white' : 'text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-[#555555]'}`} data-id="element-210">
                                                {day}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className="relative" data-id="element-211">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-212">
                              <Clock className="w-5 h-5 mr-2 text-brand-muted" data-id="element-213" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-214" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#05894d] border-[#05894d]' : ''}`} data-id="element-215">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-216">
                                {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                              </span>
                              <Clock className="w-5 h-5 text-stone-300" data-id="element-217" />
                            </button>
                            <AnimatePresence data-id="element-218">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-full bg-white dark:bg-[#4a4a4a] border border-stone-200 dark:border-[#555555] rounded-2xl shadow-2xl p-3 space-y-1" data-id="element-219">
                                  {[{
                          value: 'morning',
                          label: 'Morning (7–11 AM)'
                        }, {
                          value: 'afternoon',
                          label: 'Afternoon (12–4 PM)'
                        }, {
                          value: 'flexible',
                          label: 'Flexible'
                        }].map(opt => <button key={opt.value} type="button" onClick={() => {
                          setDeliveryTime(opt.value);
                          setShowTimeDropdown(false);
                        }} className={`w-full text-left px-4 py-3 rounded-xl text-base font-bold transition-colors ${deliveryTime === opt.value ? 'bg-[#05894d] text-white' : 'text-stone-700 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-[#555555]'}`} data-id="element-220">
                                      {opt.label}
                                    </button>)}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same day callout */}
                        <p className="text-sm text-stone-300 flex items-center gap-1.5 font-medium" data-id="element-221">
                          <Phone className="w-3.5 h-3.5 text-stone-400" data-id="element-222" />
                          Same day? Call{' '}
                          <a href="tel:206-619-3742" className="font-bold text-brand-orange hover:underline" data-id="element-223">
                            (206) 619-3742
                          </a>
                        </p>

                        {/* Instructions */}
                        <div data-id="element-224">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-225">
                            <MessageSquare className="w-5 h-5 mr-2 text-brand-muted" data-id="element-226" />
                            Special Instructions
                          </label>
                          <textarea value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Dump on left side of driveway, gate code 1234..." rows={2} className={`${inputClass} resize-none`} data-id="element-227" />
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className="px-8 pb-8 border-t border-brand-lightgray dark:border-[#4b4b4b] pt-8" data-id="element-228">
                      <div className="space-y-3 mb-6" data-id="element-229">
                        <div className="flex justify-between items-end" data-id="element-230">
                          <span className="text-brand-muted dark:text-stone-300 font-bold text-base" data-id="element-231">
                            Materials ({totalYards} yards
                            {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            )
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-232">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>
                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-233">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-234" />
                            <p className="text-sm text-stone-300 font-medium" data-id="element-235">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        <div className="flex justify-between items-center" data-id="element-236">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-237">
                            Delivery Fee
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-238">
                            {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-239" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-240">
                                Custom Quote
                              </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-241">
                                FREE
                              </span> : deliveryFee > 0 ? <>
                                ${totalDeliveryFee.toFixed(2)}
                                {deliveryCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-242">
                                    ({deliveryCount} × ${deliveryFee.toFixed(2)}
                                    )
                                  </span>}
                              </> : <span className="text-stone-500 text-sm font-medium" data-id="element-243">
                                Enter address above
                              </span>}
                          </span>
                        </div>

                        <div className="flex justify-between items-end pt-4 border-t border-brand-lightgray dark:border-[#4b4b4b]" data-id="element-244">
                          <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-245">
                            Total Estimate
                          </span>
                          <span className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-246">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-247">
                        <button type="submit" className="w-full flex items-center justify-center bg-brand-button hover:bg-brand-buttonHover text-white py-5 rounded-xl font-extrabold text-lg transition-colors group" data-id="element-248">
                          Proceed to Checkout
                          <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-249" />
                        </button>
                        <button type="button" onClick={handleContinueShopping} className="w-full py-5 rounded-xl font-extrabold text-lg text-brand-charcoal dark:text-white border-2 border-brand-lightgray dark:border-[#4b4b4b] hover:bg-brand-lightgray dark:hover:bg-[#4a4a4a] transition-colors" data-id="element-250">
                          Add More Materials
                        </button>
                      </div>
                      <p className="text-sm text-brand-charcoal/70 dark:text-white/95 text-center mt-5 font-medium" data-id="element-251">
                        Need more than 12 yards or a custom amount?{' '}
                        <a href="tel:206-619-3742" className="text-brand-orange hover:underline font-bold" data-id="element-252">
                          Call us
                        </a>{' '}
                        or{' '}
                        <a href="/contact" className="text-brand-orange hover:underline font-bold" data-id="element-253">
                          contact us
                        </a>
                        .
                      </p>
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto p-8" data-id="element-254">
                  <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-255">
                    {/* Order Summary */}
                    <div data-id="element-256">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-257">
                        Order Summary
                      </h3>
                      <div className="space-y-3" data-id="element-258">
                        {items.map(item => <div key={item.id} className="flex justify-between items-center text-base py-3 border-b border-stone-100 dark:border-[#4b4b4b] last:border-0" data-id="element-259">
                            <div data-id="element-260">
                              <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-261">
                                {item.product}
                              </span>
                              <span className="text-brand-muted dark:text-stone-400 ml-2 font-medium" data-id="element-262">
                                × {item.quantity} yds
                              </span>
                            </div>
                            <span className="font-extrabold text-brand-charcoal dark:text-white" data-id="element-263">
                              $
                              {(() => {
                        const surcharge = item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
                        return (item.quantity * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                      })()}
                            </span>
                          </div>)}
                        <div className="space-y-3 pt-3" data-id="element-264">
                          <div className="flex justify-between items-center" data-id="element-265">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-266">
                              Subtotal
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-267">
                              ${totalPrice.toFixed(2)}
                            </span>
                          </div>

                          {/* Delivery Fee Row */}
                          <div className="flex justify-between items-center" data-id="element-268">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-269">
                              Delivery Fee
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-270">
                              {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-271" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-272">
                                  Custom Quote
                                </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-273">
                                  FREE
                                </span> : deliveryFee > 0 ? <>
                                  {deliveryCount > 1 && <span className="text-sm text-stone-400 mr-1.5" data-id="element-274">
                                      ({deliveryCount} × $
                                      {deliveryFee.toFixed(2)})
                                    </span>}
                                  ${totalDeliveryFee.toFixed(2)}
                                </> : <span className="text-stone-500 text-sm font-medium" data-id="element-275">
                                  Based on address
                                </span>}
                            </span>
                          </div>

                          <div className="flex justify-between items-center pt-4 border-t border-stone-100 dark:border-[#4b4b4b]" data-id="element-276">
                            <span className="font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-277">
                              Total
                            </span>
                            <span className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-278">
                              ${finalTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Details Summary (Read-Only) */}
                    <div data-id="element-279">
                      <div className="flex items-center justify-between mb-4" data-id="element-280">
                        <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-281">
                          Delivery Details
                        </h3>
                        <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-282">
                          Edit
                        </button>
                      </div>
                      <div className="bg-brand-lightgray/30 dark:bg-[#333333] border border-brand-lightgray dark:border-[#4b4b4b] rounded-xl p-5 space-y-4" data-id="element-283">
                        <div className="flex items-start gap-3" data-id="element-284">
                          <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-285" />
                          <div data-id="element-286">
                            <p className="text-base text-brand-charcoal dark:text-white font-bold" data-id="element-287">
                              {addressInput}
                            </p>
                            {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-288">
                                Zone {deliveryZone}
                              </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-289">
                                Outside standard zones (Custom Quote)
                              </p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-290">
                          <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-291" />
                          <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-292">
                            {phone}
                          </p>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-293">
                          <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-294" />
                          <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-295">
                            {new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                            <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-296">
                              •
                            </span>
                            <span className="capitalize" data-id="element-297">{deliveryTime}</span>
                          </p>
                        </div>
                        {instructions && <div className="flex items-start gap-3 pt-3 border-t border-brand-lightgray dark:border-[#4b4b4b]" data-id="element-298">
                            <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-299" />
                            <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-300">
                              "{instructions}"
                            </p>
                          </div>}
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div data-id="element-301">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-302">
                        Payment Method
                      </h3>
                      <div className="grid grid-cols-2 gap-4 mb-5" data-id="element-303">
                        <button type="button" disabled className="flex flex-col items-center p-5 rounded-xl border-2 transition-all border-stone-200 dark:border-[#4b4b4b] bg-brand-lightgray/30 dark:bg-[#2d2d2d] opacity-50 cursor-not-allowed" data-id="element-304">
                          <CreditCard className="w-7 h-7 mb-2 text-brand-charcoal dark:text-stone-200" data-id="element-305" />
                          <span className="text-base font-extrabold text-brand-charcoal dark:text-stone-200" data-id="element-306">
                            Pay Online
                          </span>
                          <span className="text-xs text-stone-700 dark:text-stone-200 mt-1 font-medium text-center" data-id="element-307">
                            Online payment coming soon
                          </span>
                        </button>
                        <button type="button" onClick={() => setPaymentMethod('delivery')} className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all ${paymentMethod === 'delivery' ? 'border-brand-orange bg-brand-orange/10 dark:bg-brand-orange/10' : 'border-stone-200 dark:border-[#4b4b4b] bg-brand-lightgray/30 dark:bg-[#2d2d2d] hover:border-stone-300 dark:hover:border-[#5a5a5a]'}`} data-id="element-308">
                          <Banknote className={`w-7 h-7 mb-2 ${paymentMethod === 'delivery' ? 'text-brand-orange' : 'text-brand-charcoal dark:text-stone-200'}`} data-id="element-309" />
                          <span className={`text-base font-extrabold ${paymentMethod === 'delivery' ? 'text-brand-orange' : 'text-brand-charcoal dark:text-stone-200'}`} data-id="element-310">
                            Pay on Delivery
                          </span>
                          <span className="text-xs text-stone-700 dark:text-stone-200 mt-1 font-medium" data-id="element-311">
                            Cash, card, or check
                          </span>
                        </button>
                      </div>
                    </div>
                  </form>
                  {/* Checkout Footer */}
                  <div className="p-8 bg-brand-lightgray/20 dark:bg-[#333333] border-t border-brand-lightgray dark:border-[#4b4b4b] -mx-8 -mb-8 mt-8" data-id="element-312">
                    <button type="submit" form="checkout-form" disabled={isProcessingPayment} className="w-full flex items-center justify-center bg-brand-button hover:bg-brand-buttonHover text-white py-5 rounded-xl font-extrabold text-lg transition-colors group disabled:opacity-70" data-id="element-313">
                      {isProcessingPayment ? <>
                          <Loader2 className="w-6 h-6 mr-2 animate-spin" data-id="element-314" />
                          Processing Order...
                        </> : <>
                          Submit Order Request
                          <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-315" />
                        </>}
                    </button>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && <div className="flex-1 flex flex-col items-center justify-center text-center p-10 space-y-8" data-id="element-316">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 15,
              stiffness: 200
            }} className="w-24 h-24 bg-[#05894d]/20 dark:bg-[#05894d]/20 rounded-full flex items-center justify-center" data-id="element-317">
                    <CheckCircle2 className="w-12 h-12 text-[#05894d] dark:text-[#05894d]" data-id="element-318" />
                  </motion.div>
                  <div data-id="element-319">
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-320">
                      Order Request Submitted!
                    </h3>
                    <p className="text-brand-muted dark:text-stone-300 max-w-sm mx-auto text-lg font-medium" data-id="element-321">
                      You will receive a confirmation via text message or email
                      shortly. We may also call you at{' '}
                      <span className="font-bold text-brand-charcoal dark:text-white" data-id="element-322">
                        {phone}
                      </span>{' '}
                      to confirm your delivery details before bringing the
                      material.
                    </p>
                  </div>
                  <button onClick={handleCloseSuccess} className="bg-brand-charcoal dark:bg-[#4a4a4a] hover:bg-brand-charcoal/90 dark:hover:bg-[#555555] text-white px-10 py-4 rounded-xl font-extrabold text-lg transition-colors" data-id="element-323">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}